import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import propTypes from 'prop-types'
import { StyledIcon, StyledIconWrapper } from 'Shared/form/iconPickerSelect'

function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const SectionItem = ({
  sectionData: { title, id, group_id, item_type, color, icon },
  topLevelGroup,
  themeColor,
  currentSectionID,
  activeTabId,
  namespace,
  workspace,
  slug,
  version,
  isOpen,
  setIsOpen
}) => (
  <Item
    onClick={e => {
      if (item_type === 'group') setIsOpen(!isOpen)
      e.stopPropagation()
    }}
  >
    {item_type === 'section' ? (
      <>
        <ActiveMarker $themeColor={themeColor} $isActive={id.toString() === currentSectionID} />
        <Title
          to={`/${namespace}/${workspace}/${slug}/${version}/${id}`}
          $active={id.toString() === currentSectionID}
          $themeColor={themeColor}
          $hasParent={activeTabId ? group_id !== activeTabId : !!group_id}
        >
          {icon && (
            <StyledIconWrapper $inline className="me-1">
              <StyledIcon className={icon} $color={color} />
            </StyledIconWrapper>
          )}
          {title}
        </Title>
      </>
    ) : (
      <GroupTitle $themeColor={themeColor} $topLevelGroup={topLevelGroup}>
        <ActiveMarker $themeColor={themeColor} $isActive={id.toString() === currentSectionID} />
        {icon && (
          <StyledIconWrapper $inline className="me-1">
            <StyledIcon className={icon} $color={color} />
          </StyledIconWrapper>
        )}
        {title}
      </GroupTitle>
    )}
    {item_type === 'group' && (
      <GroupChevron $themeColor={themeColor}>
        {isOpen ? <i className="fa fa-chevron-down" /> : <i className="fa fa-chevron-right" />}
      </GroupChevron>
    )}
  </Item>
)

SectionItem.propTypes = {
  namespace: propTypes.string,
  workspace: propTypes.string,
  slug: propTypes.string,
  version: propTypes.string,
  // The currently navigated section ID
  currentSectionID: propTypes.string,
  activeTabId: propTypes.number,
  sectionData: propTypes.shape({
    id: propTypes.number,
    group_id: propTypes.number,
    item_type: propTypes.string,
    title: propTypes.string,
    color: propTypes.string,
    icon: propTypes.string,
    sections: propTypes.string,
    section_ids: propTypes.string
  }),
  topLevelGroup: propTypes.bool.isRequired,
  themeColor: propTypes.string,
  isOpen: propTypes.bool,
  setIsOpen: propTypes.func
}

/**
 * @typedef {object} NestedSection
 * @property {number} id
 * @property {number} group_id
 * @property {string} item_type
 * @property {string} title
 * @property {string} color
 * @property {string} icon
 * @property {string} [sections] JSONified array string. will be converted to an array of NestedSection
 * @property {string} [section_ids] JSONified array string. will be converted to an array of ids. ids will be numbers.
 */

/**
 * mapAncestorSections
 * @param {Array<NestedSection>} nested_sections
 * @param {Array<number>} idsToReturn
 * @returns {Array<number>}
 */
function mapAncestorSections(nested_sections, idsToReturn = []) {
  if (!Array.isArray(nested_sections)) {
    // Nothing to add, return
    return idsToReturn
  }
  // iterate through the array of sections, add their id to idsToReturn, recursively call this function
  nested_sections.forEach(({ id, sections, item_type }) => {
    idsToReturn.push(id)
    if (sections && item_type === 'group') {
      // eslint-disable-next-line no-param-reassign
      idsToReturn = mapAncestorSections(JSON.parse(sections), idsToReturn)
    }
  })
  return idsToReturn
}

const Section = ({
  sectionData,
  topLevelGroup,
  themeColor,
  currentSectionID,
  activeTabId,
  namespace,
  workspace,
  slug,
  version
}) => {
  const { id, group_id, item_type, sections, section_ids } = sectionData

  const [isOpen, setIsOpen] = useState(false)
  const previousSection = usePrevious(currentSectionID)

  /** @type Array<NestedSection> */
  let childrenSections = []
  /** @type Array<number> */
  let ancestorSectionIds = []

  if (sections && section_ids && item_type === 'group') {
    childrenSections = JSON.parse(sections)
    ancestorSectionIds = mapAncestorSections(childrenSections)
  }

  useEffect(() => {
    if (
      ancestorSectionIds.length &&
      ancestorSectionIds.indexOf(parseInt(currentSectionID)) !== -1 &&
      !isOpen &&
      ancestorSectionIds.indexOf(parseInt(previousSection)) === -1
    )
      setIsOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ancestorSectionIds, group_id, isOpen, currentSectionID])

  return (
    <div key={id}>
      <SectionItem
        key={id}
        sectionData={sectionData}
        topLevelGroup={topLevelGroup}
        themeColor={themeColor}
        currentSectionID={currentSectionID}
        activeTabId={activeTabId}
        namespace={namespace}
        workspace={workspace}
        slug={slug}
        version={version}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
      {item_type === 'group' && isOpen && (
        <ItemChildren>
          {childrenSections.map(childSection => (
            <Section
              key={childSection.id}
              sectionData={childSection}
              topLevelGroup={false}
              themeColor={themeColor}
              currentSectionID={currentSectionID}
              activeTabId={activeTabId}
              namespace={namespace}
              workspace={workspace}
              slug={slug}
              version={version}
            />
          ))}
        </ItemChildren>
      )}
    </div>
  )
}

Section.propTypes = {
  namespace: propTypes.string,
  workspace: propTypes.string,
  slug: propTypes.string,
  version: propTypes.string,
  // The currently navigated section ID
  currentSectionID: propTypes.string,
  activeTabId: propTypes.number,
  sectionData: propTypes.shape({
    id: propTypes.number,
    group_id: propTypes.number,
    item_type: propTypes.string,
    title: propTypes.string,
    color: propTypes.string,
    icon: propTypes.string,
    // JSONified array string. will be converted to an array of NestedSection
    sections: propTypes.string,
    // JSONified array string. will be converted to an array of ids. ids will be numbers.
    section_ids: propTypes.string
  }),
  topLevelGroup: propTypes.bool.isRequired,
  themeColor: propTypes.string
}

const Item = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 3px 8px;
  padding-left: 0px;
  border-radius: 3px;
  cursor: pointer;
  &:hover {
    background-color: ${props => props.theme.hoverBgLightestInverse};
    border-radius: 3px;
    text-decoration: none;
  }
`

const ItemChildren = styled.div`
  padding-left: 10px;
`

const GroupTitle = styled.span`
  display: flex;
  font-size: 15px;
  padding: 10px 0;
  flex: 1;
  font-weight: ${({ $topLevelGroup }) => ($topLevelGroup ? 500 : 'normal')};
  text-decoration: none !important;
  color: ${({ theme, $topLevelGroup }) =>
    $topLevelGroup ? `${theme.textDocumentGroup} !important` : `${theme.textDocumentSection} !important`};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Title = styled(Link)`
  font-size: 15px;
  padding: 10px 0;
  display: flex;
  flex: 1;
  font-weight: ${({ $hasParent }) => (!$hasParent ? `500 !important` : `normal !important`)};
  text-decoration: none !important;
  color: ${({ $active, $hasParent, theme }) =>
    $active
      ? `${theme.textDocumentGroup} !important`
      : !$hasParent
        ? `${theme.textDocumentGroup} !important`
        : `${theme.textDocumentSection} !important`};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const GroupChevron = styled.span`
  margin-left: 10px;
  color: ${props => props.theme.textGreyTintedLighterInverse};
  font-size: 11px;
`

const ActiveMarker = styled.div`
  height: 22px;
  width: 0.219rem;
  border-radius: 6px;
  background-color: ${props => (props.$isActive ? '#884ffc' : 'transparent')};
  margin: 0px 8px;
`

export default Section
